import {CommonModule, DatePipe} from '@angular/common';
import {ModuleWithProviders, NgModule} from '@angular/core';
import {FlexLayoutModule} from '@angular/flex-layout';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatPaginatorIntl} from '@angular/material/paginator';
import {RouterModule} from '@angular/router';
import {PaginatorI18n} from '@app/shared/utils/paginator-i18n';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {NgxChartsModule} from '@swimlane/ngx-charts';
import {FileUploadModule} from 'ng2-file-upload';
import {CustomDateComponent} from '../admin/customer-controlled-modules/modules/custom-date/custom-date.component';
import {AppMaterialModule} from '../app-material.module';
import {AnnouncementsTableDialogComponent} from './announcements-table/announcements-table-dialog.component';
import {AnnouncementsTableComponent} from './announcements-table/announcements-table.component';
import {BlacklistComponent} from './blacklist/blacklist.component';
import {BreadcrumbComponent} from './breadcrumb/breadcrumb.component';
import {CallLogComponent} from './call-log/call-log.component';
import {MainNumberTableComponent} from './call-log/main-number-table/main-number-table.component';
import {StatisticsChartComponent} from './call-log/statistics-chart/statistics-chart.component';
import {CardWrapperComponent} from './card-wrapper/card-wrapper.component';
import {CdkDetailRowDirective} from './cdk-detail-row.directive';
import {Click2callComponent} from './click2call/click2call.component';
import {ContactAvatarComponent} from './contact-avatar/contact-avatar.component';
import {ContactDetailComponent} from './contact-detail/contact-detail.component';
import {ContactsComponent} from './contacts/contacts.component';
import {CsvContactsImportComponent} from './csv-contacts-import/csv-contacts-import.component';
import {DatetimeInputComponent} from './datetime-input/datetime-input.component';
import {DialogHeaderComponent} from './dialog-header/dialog-header.component';
import {DisableWhenNotLoggedInDirective} from './directives/gt-logged-in.directive';
import {GtHasAllOfProductsDirective, GtHasOneOfProductsDirective} from './directives/gt-products.directive';
import {DisplayNumberSelectorComponent} from './display-number-selector/display-number-selector.component';
import {DisplayNumberSettingsComponent} from './display-number-settings/display-number-settings.component';
import {EditableFieldComponent} from './editable-field/editable-field.component';
import {EuCallLogComponent} from './eu-call-log/eu-call-log.component';
import {
  CallCauseString,
  DurationFormatter,
  DurationSince,
  EndTimeFormatter,
  EndTimeFullFormatter,
  EndTimeShortFormatter,
  ForwardingDelay,
  ForwardingText,
  MinuteDurationFormat,
  NameAndNumber,
  PhoneNumberFormatter,
  ServicePattern,
  ShortDatePipe,
  ShortDateTimePipe,
  TimeAndDateFormatter,
  TimeAndDateFullFormatter,
  TimeLocalization,
  WorkingHourText
} from './formatting.pipes';
import {InputDialogComponent} from './input-dialog/input-dialog.component';
import {MiniCalendarComponent} from './mini-calendar/mini-calendar.component';
import {NavigationItemComponent} from './navigation-item/navigation-item.component';
import {PhoneNumberListComponent} from './phone-number-list/phone-number-list.component';
import {PhoneNumberSelectorComponent} from './phone-number-selector/phone-number-selector.component';
import {PhoneNumberAdderComponent} from './phonenumber-adder/phone-number-adder.component';
import {ProfileComponent} from './profile/profile.component';
import {QueueComponent} from './queue/queue.component';
import {RedirectHistoryComponent} from './redirect-history/redirect-history.component';
import {SkillEditorComponent} from './skill-editor/skill-editor.component';
import {TabMenuComponent} from './tab-menu/tab-menu.component';
import {TimeInputComponent} from './time-input/time-input.component';
import {EwsSyncSettingsComponent} from './user/ews-sync-settings/ews-sync-settings.component';
import {ForwardingSettingsComponent} from './user/forwarding-settings/forwarding-settings.component';
import {AbsenceForwardingSettingsComponent} from './user/status-settings/absence-forwarding-settings/absence-forwarding-settings.component';
import {DurationSettingsComponent} from './user/status-settings/duration-settings/duration-settings.component';
import {MyserviceSettingsComponent} from './user/status-settings/myservice-settings/myservice-settings.component';
import {StatusSettingsComponent} from './user/status-settings/status-settings.component';
import {StatusComponent} from './user/status/status.component';
import {TwinningSettingsComponent} from './user/twinning-settings/twinning-settings.component';
import {UserSettingsComponent} from './user/user-settings/user-settings.component';
import {AlphabeticValidatorDirective} from './validators/wraper-directives/alphabetic-validator.directive';
import {AlphanumericValidatorDirective} from './validators/wraper-directives/alphanumeric-validator.directive';
import {NotExistsInArrayValidatorDirective} from './validators/wraper-directives/exists-in-array-validator.directive';
import {MaxCharactersValidatorDirective} from './validators/wraper-directives/max-characters-validator.directive';
import {NumericValidatorDirective} from './validators/wraper-directives/numeric-validator.directive';
import {PhoneNumberValidatorDirective} from './validators/wraper-directives/phone-number-validator.directive';
import {DialerComponent} from './webrtc-phone/dialer/dialer.component';
import {WebRTCPhoneComponent} from './webrtc-phone/webrtc-phone.component';
import {WhitelistComponent} from './whitelist/whitelist.component';
import {WorkingHoursComponent} from './working-hours/working-hours.component';
import {YellowPagesSearchComponent} from './yellow-pages-search/yellow-pages-search.component';
import {ConfirmDeleteDialogComponent} from '@app/shared/confirm-delete-dialog/confirm-delete-dialog.component';
import {GtDebounceClickDirective} from '@app/shared/directives/gt-debounce-click.directive';

export function paginatorTranslatorLoader(translateService: TranslateService) {
  return new PaginatorI18n(translateService).getPaginatorIntl();
}

@NgModule({
  declarations: [
    AlphabeticValidatorDirective,
    AlphanumericValidatorDirective,
    AnnouncementsTableComponent,
    AnnouncementsTableDialogComponent,
    BlacklistComponent,
    BreadcrumbComponent,
    CallCauseString,
    CallLogComponent,
    CardWrapperComponent,
    CdkDetailRowDirective,
    Click2callComponent,
    ContactAvatarComponent,
    ContactDetailComponent,
    ContactsComponent,
    CsvContactsImportComponent,
    CustomDateComponent,
    DatetimeInputComponent,
    DialerComponent,
    DialogHeaderComponent,
    DisableWhenNotLoggedInDirective,
    DisplayNumberSelectorComponent,
    DisplayNumberSettingsComponent,
    DurationFormatter,
    DurationSince,
    EditableFieldComponent,
    EuCallLogComponent,
    ForwardingDelay,
    ForwardingText,
    GtHasAllOfProductsDirective,
    GtHasOneOfProductsDirective,
    InputDialogComponent,
    MainNumberTableComponent,
    MaxCharactersValidatorDirective,
    MiniCalendarComponent,
    MinuteDurationFormat,
    NameAndNumber,
    NavigationItemComponent,
    NotExistsInArrayValidatorDirective,
    NumericValidatorDirective,
    PhoneNumberAdderComponent,
    PhoneNumberFormatter,
    PhoneNumberListComponent,
    PhoneNumberSelectorComponent,
    PhoneNumberValidatorDirective,
    ProfileComponent,
    QueueComponent,
    RedirectHistoryComponent,
    ShortDatePipe,
    ShortDateTimePipe,
    SkillEditorComponent,
    StatisticsChartComponent,
    TabMenuComponent,
    TimeAndDateFormatter,
    TimeAndDateFullFormatter,
    TimeInputComponent,
    TimeLocalization,
    StatusSettingsComponent,
    StatusComponent,
    UserSettingsComponent,
    WebRTCPhoneComponent,
    WhitelistComponent,
    WorkingHoursComponent,
    WorkingHourText,
    YellowPagesSearchComponent,
    AbsenceForwardingSettingsComponent,
    DurationSettingsComponent,
    MyserviceSettingsComponent,
    ForwardingSettingsComponent,
    EwsSyncSettingsComponent,
    TwinningSettingsComponent,
    ConfirmDeleteDialogComponent,
    ServicePattern,
    EndTimeFullFormatter,
    EndTimeShortFormatter,
    EndTimeFormatter,
    GtDebounceClickDirective
  ],
  imports: [
    CommonModule,
    AppMaterialModule,
    FormsModule,
    ReactiveFormsModule,
    FlexLayoutModule,
    RouterModule,
    FileUploadModule,
    TranslateModule,
    NgxChartsModule
  ],
  exports: [
    CommonModule,
    AppMaterialModule,
    FormsModule,
    ReactiveFormsModule,
    FlexLayoutModule,
    NgxChartsModule,
    FileUploadModule,
    TranslateModule,
    AlphabeticValidatorDirective,
    AlphanumericValidatorDirective,
    AnnouncementsTableComponent,
    AnnouncementsTableDialogComponent,
    BlacklistComponent,
    BreadcrumbComponent,
    CallCauseString,
    CallLogComponent,
    CardWrapperComponent,
    CdkDetailRowDirective,
    Click2callComponent,
    ContactDetailComponent,
    ContactsComponent,
    CustomDateComponent,
    DatetimeInputComponent,
    DialogHeaderComponent,
    DisplayNumberSelectorComponent,
    DisplayNumberSettingsComponent,
    DurationFormatter,
    DurationSince,
    EditableFieldComponent,
    EuCallLogComponent,
    ForwardingDelay,
    ForwardingText,
    GtHasAllOfProductsDirective,
    GtHasOneOfProductsDirective,
    InputDialogComponent,
    MaxCharactersValidatorDirective,
    MinuteDurationFormat,
    NavigationItemComponent,
    NumericValidatorDirective,
    PhoneNumberAdderComponent,
    PhoneNumberFormatter,
    PhoneNumberListComponent,
    PhoneNumberSelectorComponent,
    PhoneNumberValidatorDirective,
    QueueComponent,
    RedirectHistoryComponent,
    ShortDatePipe,
    ShortDateTimePipe,
    SkillEditorComponent,
    StatusComponent,
    TabMenuComponent,
    TimeAndDateFormatter,
    TimeAndDateFullFormatter,
    TimeInputComponent,
    TimeLocalization,
    UserSettingsComponent,
    WebRTCPhoneComponent,
    WhitelistComponent,
    WorkingHoursComponent,
    WorkingHourText,
    YellowPagesSearchComponent,
    ConfirmDeleteDialogComponent,
    ServicePattern,
    EndTimeFullFormatter,
    EndTimeShortFormatter,
    EndTimeFormatter,
    GtDebounceClickDirective
  ]
})
export class SharedModule {
  static forRoot(): ModuleWithProviders<SharedModule> {
    return {
      ngModule: SharedModule,
      providers: [
        TimeAndDateFullFormatter,
        PhoneNumberFormatter,
        WorkingHourText,
        DatePipe,
        TimeLocalization,
        DurationFormatter,
        TimeAndDateFormatter,
        CallCauseString,
        MinuteDurationFormat,
        {
          provide: MatPaginatorIntl,
          deps: [TranslateService],
          useFactory: paginatorTranslatorLoader
        }
      ]
    };
  }
}
